/*
  Tansy · base "mm": lista de desejos, quantidade e cores do cartão, e o carrossel <velune-mm-carousel>.
  Carregado em todas as páginas por snippets/velune-global.liquid.

  window.VeluneWishlist (lista de desejos guardada no navegador, sem conta)
    .handles / .items / .list()   cópia da lista de handles
    .count                         quantos produtos
    .has(handle) .add(handle) .remove(handle) .toggle(handle) .clear()
    Evento "velune:wishlist-change" no document (sobe até a window), com
    detail = { handles, count, handle, added }. Também é disparado uma vez na carga da página,
    para quem mostra a contagem (cabeçalho) não depender da ordem dos scripts.
*/

/* ------------------------------------------------------------------ */
/* Lista de desejos                                                   */
/* ------------------------------------------------------------------ */

const WISHLIST_KEY = 'velune:wishlist';
const WISHLIST_EVENT = 'velune:wishlist-change';

/** @returns {string[]} */
function readWishlist() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(WISHLIST_KEY) || '[]');
    if (!Array.isArray(stored)) return [];
    return [...new Set(stored.filter((item) => typeof item === 'string' && item !== ''))];
  } catch {
    return [];
  }
}

/** Cópia em memória: o coração continua funcionando na página mesmo sem acesso ao localStorage. */
let wishlistHandles = readWishlist();

function writeWishlist() {
  try {
    window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(wishlistHandles));
  } catch {
    /* navegação privada ou armazenamento cheio: vale só para esta página */
  }
}

/**
 * @param {string | null} [handle]
 * @param {boolean | null} [added]
 */
function emitWishlistChange(handle = null, added = null) {
  syncWishlistButtons();
  document.dispatchEvent(
    new CustomEvent(WISHLIST_EVENT, {
      bubbles: true,
      detail: { handles: [...wishlistHandles], count: wishlistHandles.length, handle, added },
    })
  );
}

const VeluneWishlist = {
  eventName: WISHLIST_EVENT,
  storageKey: WISHLIST_KEY,

  get handles() {
    return [...wishlistHandles];
  },

  get items() {
    return [...wishlistHandles];
  },

  get count() {
    return wishlistHandles.length;
  },

  list() {
    return [...wishlistHandles];
  },

  /** @param {string} handle */
  has(handle) {
    return wishlistHandles.includes(handle);
  },

  /** @param {string} handle */
  add(handle) {
    if (!handle || wishlistHandles.includes(handle)) return false;
    wishlistHandles = [...wishlistHandles, handle];
    writeWishlist();
    emitWishlistChange(handle, true);
    return true;
  },

  /** @param {string} handle */
  remove(handle) {
    if (!wishlistHandles.includes(handle)) return false;
    wishlistHandles = wishlistHandles.filter((item) => item !== handle);
    writeWishlist();
    emitWishlistChange(handle, false);
    return true;
  },

  /**
   * @param {string} handle
   * @returns {boolean} true quando o produto ficou na lista
   */
  toggle(handle) {
    if (!handle) return false;
    if (wishlistHandles.includes(handle)) {
      this.remove(handle);
      return false;
    }
    this.add(handle);
    return true;
  },

  clear() {
    if (wishlistHandles.length === 0) return;
    wishlistHandles = [];
    writeWishlist();
    emitWishlistChange(null, false);
  },
};

// @ts-ignore
window.VeluneWishlist = VeluneWishlist;

/** Marca e desmarca os corações que estão na página. */
function syncWishlistButtons() {
  for (const button of document.querySelectorAll('[data-mm-wish]')) {
    if (!(button instanceof HTMLElement)) continue;
    const active = wishlistHandles.includes(button.dataset.handle || '');
    if (button.getAttribute('aria-pressed') !== String(active)) {
      button.setAttribute('aria-pressed', String(active));
    }
    const label = active ? button.dataset.labelRemove : button.dataset.labelAdd;
    if (label && button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
  }
}

// Outra aba mudou a lista
window.addEventListener('storage', (event) => {
  if (event.key !== WISHLIST_KEY) return;
  wishlistHandles = readWishlist();
  emitWishlistChange(null, null);
});

/* ------------------------------------------------------------------ */
/* Cartão: coração, quantidade, cores e botão de adicionar            */
/* ------------------------------------------------------------------ */

const QTY_MAX = 99;

/** @param {HTMLInputElement} input */
function clampQuantity(input) {
  const min = Number.parseInt(input.min, 10) || 1;
  const max = Number.parseInt(input.max, 10) || QTY_MAX;
  const value = Number.parseInt(input.value, 10);
  const clamped = Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
  if (String(clamped) !== input.value) input.value = String(clamped);

  const wrapper = input.closest('[data-mm-qty]');
  const minus = wrapper?.querySelector('[data-mm-qty-step="-1"]');
  const plus = wrapper?.querySelector('[data-mm-qty-step="1"]');
  if (minus instanceof HTMLButtonElement) minus.disabled = clamped <= min;
  if (plus instanceof HTMLButtonElement) plus.disabled = clamped >= max;
  return clamped;
}

/** Quantidade pedida no cartão cujo botão abriu a janela de escolha. */
let pendingQuantity = 0;

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const wish = target.closest('[data-mm-wish]');
    if (wish instanceof HTMLElement) {
      event.preventDefault();
      VeluneWishlist.toggle(wish.dataset.handle || '');
      return;
    }

    const stepButton = target.closest('[data-mm-qty-step]');
    if (stepButton instanceof HTMLElement) {
      event.preventDefault();
      const input = stepButton.closest('[data-mm-qty]')?.querySelector('input');
      if (input instanceof HTMLInputElement) {
        const step = Number.parseInt(stepButton.dataset.mmQtyStep || '0', 10) || 0;
        input.value = String((Number.parseInt(input.value, 10) || 1) + step);
        clampQuantity(input);
      }
      return;
    }

    const swatchArrow = target.closest('[data-mm-sw-prev], [data-mm-sw-next]');
    if (swatchArrow instanceof HTMLElement) {
      event.preventDefault();
      const track = swatchArrow.closest('[data-mm-swatches]')?.querySelector('[data-mm-sw-track]');
      if (track instanceof HTMLElement) {
        const direction = swatchArrow.hasAttribute('data-mm-sw-next') ? 1 : -1;
        track.scrollBy({ left: direction * Math.max(52, track.clientWidth - 26) });
      }
      return;
    }

    const choose = target.closest('[data-mm-choose]');
    if (choose instanceof HTMLElement) {
      const input = choose.closest('.mm-card')?.querySelector('.mm-qty__input');
      pendingQuantity =
        input instanceof HTMLInputElement && input.offsetParent !== null ? clampQuantity(input) : 0;

      // Sem a compra rápida do tema (desligada nas configurações), o botão leva à página do produto
      const quickAddReady = customElements.get('quick-add-component') && document.getElementById('quick-add-dialog');
      if (!quickAddReady && choose.dataset.url) {
        event.preventDefault();
        window.location.href = choose.dataset.url;
      }
    }
  },
  true
);

document.addEventListener('change', (event) => {
  if (event.target instanceof HTMLInputElement && event.target.matches('.mm-qty__input')) {
    clampQuantity(event.target);
  }
});

/** Leva a quantidade escolhida no cartão para a janela de escolha do tema, quando ela abre. */
function watchQuickAddDialog() {
  const dialog = document.getElementById('quick-add-dialog');
  if (!dialog || dialog.hasAttribute('data-mm-watched')) return;
  dialog.setAttribute('data-mm-watched', '');

  dialog.addEventListener('dialog:open', () => {
    const quantity = pendingQuantity;
    pendingQuantity = 0;
    if (quantity < 2) return;

    const selector = dialog.querySelector('quantity-selector-component');
    const input = selector?.querySelector('input[name="quantity"]');
    if (!(input instanceof HTMLInputElement)) return;

    const max = Number.parseInt(input.max, 10);
    input.value = String(Number.isFinite(max) && max > 0 ? Math.min(max, quantity) : quantity);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

/** Mostra as setas da faixa de cores só quando há mais cores do que cabem. */
function syncSwatchStrip(strip) {
  const track = strip.querySelector('[data-mm-sw-track]');
  const prev = strip.querySelector('[data-mm-sw-prev]');
  const next = strip.querySelector('[data-mm-sw-next]');
  if (!(track instanceof HTMLElement) || !(prev instanceof HTMLElement) || !(next instanceof HTMLElement)) return;

  // As setas ocupam 60 px (2 × 24 + 2 vãos de 6). Sem elas, a faixa mede a largura inteira.
  const arrowsSpace = prev.hidden ? 0 : 60;
  const overflows = track.scrollWidth > track.clientWidth + arrowsSpace + 1;
  if (prev.hidden === overflows) {
    prev.hidden = !overflows;
    next.hidden = !overflows;
  }
}

/** @type {ResizeObserver | null} */
const swatchObserver =
  'ResizeObserver' in window
    ? new ResizeObserver((entries) => {
        for (const entry of entries) syncSwatchStrip(entry.target);
      })
    : null;

function watchSwatchStrips() {
  for (const strip of document.querySelectorAll('[data-mm-swatches]:not([data-mm-watched])')) {
    strip.setAttribute('data-mm-watched', '');
    syncSwatchStrip(strip);
    swatchObserver?.observe(strip);
  }
}

/** Cartões novos (rolagem infinita, recomendações, editor de temas) recebem o mesmo tratamento. */
let scanQueued = false;

function scanPage() {
  scanQueued = false;
  syncWishlistButtons();
  watchSwatchStrips();
  watchQuickAddDialog();
}

function queueScan() {
  if (scanQueued) return;
  scanQueued = true;
  requestAnimationFrame(scanPage);
}

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches('.mm-card, [data-mm-wish]') || node.querySelector('.mm-card, [data-mm-wish]')) {
        queueScan();
        return;
      }
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

let firstRunDone = false;

function firstRun() {
  if (firstRunDone) return;
  firstRunDone = true;
  scanPage();
  emitWishlistChange(null, null);
}

if (document.readyState === 'complete') {
  firstRun();
} else {
  // Scripts de módulo rodam antes do DOMContentLoaded: quem escuta o evento já está pronto quando ele sai.
  document.addEventListener('DOMContentLoaded', firstRun, { once: true });
  window.addEventListener('load', firstRun, { once: true });
  scanPage();
}

/* ------------------------------------------------------------------ */
/* Carrossel                                                          */
/* ------------------------------------------------------------------ */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class VeluneMmCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLButtonElement | null} */
  #prev = null;
  /** @type {HTMLButtonElement | null} */
  #next = null;
  /** @type {HTMLElement | null} */
  #dots = null;
  /** @type {HTMLElement | null} */
  #progress = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {MutationObserver | null} */
  #mutationObserver = null;
  /** @type {AbortController | null} */
  #abort = null;
  #frame = 0;
  /** Posição de rolagem de cada slide. @type {number[]} */
  #positions = [];
  /** Índice do primeiro slide de cada página. @type {number[]} */
  #pageStarts = [0];
  #maxIndex = 0;
  #dotsSignature = '';

  connectedCallback() {
    this.#track = this.querySelector('[data-mm-track]');
    if (!this.#track) return;

    const prev = this.querySelector('[data-mm-prev]');
    const next = this.querySelector('[data-mm-next]');
    this.#prev = prev instanceof HTMLButtonElement ? prev : null;
    this.#next = next instanceof HTMLButtonElement ? next : null;
    this.#dots = this.querySelector('[data-mm-dots]');
    this.#progress = this.querySelector('[data-mm-progress]');

    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#syncPerView();

    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'group');
      this.setAttribute('aria-roledescription', 'carousel');
    }
    this.#progress?.setAttribute('aria-hidden', 'true');

    this.#prev?.addEventListener('click', () => this.previous(), { signal });
    this.#next?.addEventListener('click', () => this.next(), { signal });
    this.#track.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    this.#dots?.addEventListener(
      'click',
      (event) => {
        const dot = event.target instanceof Element ? event.target.closest('[data-mm-page]') : null;
        if (dot instanceof HTMLElement) this.goToPage(Number(dot.dataset.mmPage) || 0);
      },
      { signal }
    );

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.refresh());
      this.#resizeObserver.observe(this.#track);
    } else {
      window.addEventListener('resize', () => this.refresh(), { signal });
    }

    this.#mutationObserver = new MutationObserver(() => this.refresh());
    this.#mutationObserver.observe(this.#track, { childList: true });

    this.refresh();
    this.setAttribute('data-mm-ready', '');
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#dotsSignature = '';
  }

  /** @returns {HTMLElement[]} */
  get slides() {
    if (!this.#track) return [];
    return /** @type {HTMLElement[]} */ (
      Array.from(this.#track.children).filter((child) => child instanceof HTMLElement && !child.hidden)
    );
  }

  /** Colunas à vista, lidas do CSS (a largura dos slides e o script usam o mesmo valor). */
  get perView() {
    const value = Number.parseFloat(getComputedStyle(this).getPropertyValue('--mm-pv'));
    return value > 0 ? value : 1;
  }

  /** Linhas por página, lidas do CSS: 2 quando a vitrine mostra "2 em cima e 2 embaixo" (só abaixo de 600 px). */
  get rows() {
    const value = Number.parseInt(getComputedStyle(this).getPropertyValue('--mm-rows'), 10);
    return value > 1 ? value : 1;
  }

  /** data-loop="true": depois do último volta o primeiro, e as setas não desligam. */
  get loop() {
    return this.dataset.loop === 'true';
  }

  /** "page": a seta passa uma página; "1": passa um cartão. */
  get mode() {
    const step = this.dataset.step;
    if (step === 'page' || step === '1') return step;
    const hasProgress = this.dataset.progress === 'true' || this.classList.contains('mm-carousel--progress');
    return hasProgress && this.dataset.dots !== 'true' ? '1' : 'page';
  }

  get index() {
    const left = this.#track?.scrollLeft ?? 0;
    let closest = 0;
    let distance = Infinity;
    this.#positions.forEach((position, index) => {
      const delta = Math.abs(position - left);
      if (delta < distance - 0.5) {
        distance = delta;
        closest = index;
      }
    });
    return Math.min(closest, this.#maxIndex);
  }

  get page() {
    const index = this.index;
    if (index >= this.#maxIndex) return this.#pageStarts.length - 1;
    let page = 0;
    this.#pageStarts.forEach((start, position) => {
      if (start <= index) page = position;
    });
    return page;
  }

  next() {
    this.#move(1);
  }

  previous() {
    this.#move(-1);
  }

  /** @param {number} index */
  goTo(index) {
    if (!this.#track) return;
    const target = Math.max(0, Math.min(this.#maxIndex, Math.round(index)));
    this.#track.scrollTo({ left: this.#positions[target] ?? 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  /** @param {number} page */
  goToPage(page) {
    const target = Math.max(0, Math.min(this.#pageStarts.length - 1, Math.round(page)));
    this.goTo(this.#pageStarts[target] ?? 0);
  }

  /** @param {1 | -1} direction */
  #move(direction) {
    const index = this.index;

    if (this.loop) {
      if (direction > 0 && index >= this.#maxIndex) {
        this.goTo(0);
        return;
      }
      if (direction < 0 && index <= 0) {
        this.goTo(this.#maxIndex);
        return;
      }
    }

    if (this.mode === '1') {
      this.goTo(index + direction);
      return;
    }

    if (direction > 0) {
      const nextStart = this.#pageStarts.find((start) => start > index);
      this.goTo(nextStart ?? this.#maxIndex);
    } else {
      const previousStarts = this.#pageStarts.filter((start) => start < index);
      this.goTo(previousStarts.length ? previousStarts[previousStarts.length - 1] ?? 0 : 0);
    }
  }

  /** Colunas pedidas nos atributos viram variáveis (cobre valores que o CSS não lista). */
  #syncPerView() {
    const desktop = Number.parseFloat(this.dataset.perView || '');
    const tablet = Number.parseFloat(this.dataset.perViewTablet || '');
    const small = Number.parseFloat(this.dataset.perViewSmall || '');
    const mobile = Number.parseFloat(this.dataset.perViewMobile || '');

    if (desktop > 0) this.style.setProperty('--mm-pv-d', String(desktop));
    if (tablet > 0) this.style.setProperty('--mm-pv-t', String(tablet));
    else if (desktop > 0) this.style.setProperty('--mm-pv-t', String(Math.min(desktop, 3)));

    const phone = mobile > 0 ? mobile : desktop > 0 ? Math.min(desktop, 2) : 0;
    if (phone > 0) this.style.setProperty('--mm-pv-m', String(phone));

    // De 600 a 768 px (data-per-view-small). Sem o atributo, a mesma regra do CSS: o carrossel de menos de 2
    // por vez (a capa) continua como no celular; os outros mostram pelo menos 3, como a grade de coleção.
    if (small > 0) this.style.setProperty('--mm-pv-s', String(small));
    else if (phone > 0) this.style.setProperty('--mm-pv-s', String(phone < 2 ? phone : Math.max(phone, 3)));
  }

  /** Mede de novo: posições, páginas, pontos, setas e barra. */
  refresh() {
    const track = this.#track;
    if (!track) return;

    // O encaixe respeita a folga real do trilho (quem zera o padding, como a capa, zera o encaixe junto)
    const padding = getComputedStyle(track).paddingLeft;
    if (track.style.scrollPaddingInline !== padding) track.style.scrollPaddingInline = padding;

    const slides = this.slides;
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const base = slides[0]?.offsetLeft ?? 0;

    this.#positions = slides.map((slide) => Math.min(slide.offsetLeft - base, maxScroll));

    const endIndex = this.#positions.findIndex((position) => position >= maxScroll - 1);
    this.#maxIndex = maxScroll <= 1 ? 0 : endIndex === -1 ? slides.length - 1 : endIndex;

    const visible = Math.max(1, Math.floor(this.perView + 0.01)) * this.rows;
    const starts = [];
    for (let start = 0; start < this.#maxIndex; start += visible) starts.push(start);
    starts.push(this.#maxIndex);
    this.#pageStarts = starts;

    const mode = this.mode;
    if (this.dataset.mmMode !== mode) this.dataset.mmMode = mode;
    slides.forEach((slide, index) => slide.classList.toggle('is-snap', mode === '1' || starts.includes(index)));

    this.toggleAttribute('data-mm-static', this.#maxIndex === 0);
    this.#renderDots();
    this.#update();
  }

  #renderDots() {
    if (!this.#dots) return;

    const count = this.#pageStarts.length;
    const signature = String(count);
    if (signature === this.#dotsSignature && this.#dots.childElementCount === count) return;
    this.#dotsSignature = signature;

    const fragment = document.createDocumentFragment();
    for (let page = 0; page < count; page += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'mm-carousel__dot';
      dot.dataset.mmPage = String(page);
      dot.setAttribute('aria-label', `Go to page ${page + 1} of ${count}`);
      fragment.append(dot);
    }
    this.#dots.replaceChildren(fragment);
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const track = this.#track;
    if (!track) return;

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const left = Math.max(0, Math.min(maxScroll, track.scrollLeft));

    const loop = this.loop;
    if (this.#prev) this.#prev.disabled = !loop && left <= 1;
    if (this.#next) this.#next.disabled = !loop && left >= maxScroll - 1;

    if (this.#dots) {
      const page = this.page;
      Array.from(this.#dots.children).forEach((dot, index) => {
        if (index === page) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    if (this.#progress) {
      // Um passo por cartão que ainda entra: começa em 1/passos e chega a 100% no fim, como na referência
      const steps = this.#maxIndex + 1;
      const ratio = maxScroll > 0 ? left / maxScroll : 1;
      const fill = (1 + ratio * (steps - 1)) / steps;
      this.#progress.style.setProperty('--mm-progress', `${(fill * 100).toFixed(2)}%`);
      // Número de 0 a 1 para a barra crescer com transform (só transform e opacity animam)
      this.#progress.style.setProperty('--mm-progress-n', fill.toFixed(4));
    }
  }
}

if (!customElements.get('velune-mm-carousel')) {
  customElements.define('velune-mm-carousel', VeluneMmCarousel);
}

/* ------------------------------------------------------------------ */
/* Arrastar para fechar (revisão de design, 16/10/2026)               */
/* Gaveta do carrinho e lista de desejos (fecham para a direita) e    */
/* menu do celular (fecha para a esquerda). Regras da apple-design:   */
/* - a gaveta segue o dedo 1:1 desde o primeiro movimento, a partir   */
/*   de onde foi pega (seções 1 e 2);                                 */
/* - dá para pegar no meio da entrada: o movimento para onde está e o */
/*   dedo continua dali (seção 3);                                    */
/* - para o lado de dentro ela resiste e quase não anda (seção 9);    */
/* - ao soltar, a velocidade do dedo projeta onde a gaveta iria parar */
/*   (seção 6): passou da metade, fecha a partir de onde está; senão, */
/*   volta com curva de mola sem quique;                              */
/* - só transform e opacity mudam; o véu acompanha (--mm-arrasto).    */
/* Gesto vertical (rolar a lista), campos e carrosséis não contam.    */
/* ------------------------------------------------------------------ */

const ARRASTO_ALVOS = [
  {
    seletor: '#cart-drawer .theme-drawer__dialog',
    lado: 1,
    fechar: () => /** @type {any} */ (document.getElementById('cart-drawer'))?.close?.(),
  },
  { seletor: '.mm-desejos', lado: 1, fechar: (painel) => painel.dispatchEvent(new Event('cancel', { cancelable: true })) },
  { seletor: '.mm-gaveta', lado: -1, fechar: (painel) => painel.dispatchEvent(new Event('cancel', { cancelable: true })) },
];

const ARRASTO_LIMIAR = 10;
const MOLA_SEM_QUIQUE = 'cubic-bezier(0.32, 0.72, 0, 1)';

/** Resistência na borda: quanto mais passa, menos acompanha (função da apple-design, seção 9) */
const elastico = (passou, tamanho, k = 0.55) => (passou * tamanho * k) / (tamanho + k * Math.abs(passou));

/** Onde o gesto pararia, pela velocidade (px/s), como a rolagem do sistema (apple-design, seção 6) */
const projetar = (velocidade, desaceleracao = 0.998) => ((velocidade / 1000) * desaceleracao) / (1 - desaceleracao);

const menosMovimento = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @type {null | { painel: HTMLElement, alvo: any, x0: number, y0: number, base: number, largura: number, decidido: boolean | null, pos: number, amostras: {t:number,p:number}[] }} */
let arrasto = null;

function deslocamentoAtual(painel) {
  const m = new DOMMatrixReadOnly(getComputedStyle(painel).transform);
  return m.m41;
}

function soltarPainel(painel) {
  painel.style.removeProperty('transform');
  painel.style.removeProperty('--mm-arrasto');
  painel.classList.remove('mm-solto');
}

function prepararPainel(painel) {
  if (painel.dataset.mmArrasto) return;
  painel.dataset.mmArrasto = '1';
  painel.addEventListener('close', () => soltarPainel(painel));
}

document.addEventListener(
  'touchstart',
  (event) => {
    arrasto = null;
    const t = event.touches[0];
    const el = event.target;
    if (event.touches.length !== 1 || !t || !(el instanceof Element)) return;
    const alvo = ARRASTO_ALVOS.find((a) => el.closest(a.seletor));
    if (!alvo) return;
    const painel = /** @type {HTMLElement} */ (el.closest(alvo.seletor));
    if (!painel.matches('[open]')) return;
    // fechando já não se pega (o fecho termina sozinho); campos e listas que rolam para o lado também não
    if (painel.matches('.mm-saindo, .theme-drawer__dialog--closing')) return;
    if (el.closest('input, select, textarea, [contenteditable], .mm-carousel__track, .velune-upsell__track, [data-mm-sem-arrasto]')) return;
    for (let n = el; n && n !== painel; n = n.parentElement) {
      if (n.scrollWidth > n.clientWidth + 1 && /auto|scroll/.test(getComputedStyle(n).overflowX)) return;
    }
    prepararPainel(painel);
    // Pegou no meio da entrada ou da volta: o movimento para onde está e o dedo segue dali
    const agora = deslocamentoAtual(painel);
    for (const a of painel.getAnimations()) a.cancel();
    if (agora) painel.style.transform = `translateX(${agora}px)`;
    arrasto = {
      painel,
      alvo,
      x0: t.clientX,
      y0: t.clientY,
      base: agora * alvo.lado,
      largura: painel.getBoundingClientRect().width || 1,
      decidido: agora ? true : null,
      pos: agora * alvo.lado,
      amostras: [{ t: event.timeStamp, p: agora * alvo.lado }],
    };
  },
  { passive: true }
);

document.addEventListener(
  'touchmove',
  (event) => {
    if (!arrasto) return;
    const t = event.touches[0];
    if (!t) return;
    const dx = t.clientX - arrasto.x0;
    const dy = t.clientY - arrasto.y0;
    if (arrasto.decidido === null) {
      if (Math.abs(dx) < ARRASTO_LIMIAR && Math.abs(dy) < ARRASTO_LIMIAR) return;
      // Só vale o gesto que vai para o lado de fechar e é mais de lado do que de cima para baixo
      arrasto.decidido = Math.abs(dx) > Math.abs(dy) * 1.2 && Math.sign(dx) === arrasto.alvo.lado;
      if (!arrasto.decidido) {
        arrasto = null;
        return;
      }
    }
    if (!arrasto.decidido) return;
    event.preventDefault();
    // Para dentro, a gaveta não passa da borda da tela: abriria uma fresta com a página atrás. Fica um
    // resto de resistência (no máximo 10 px) para o dedo sentir que chegou ao fim, sem fresta visível.
    let pos = arrasto.base + dx * arrasto.alvo.lado;
    if (pos < 0) pos = -elastico(-pos, 10);
    arrasto.pos = pos;
    const { painel, alvo, largura, amostras } = arrasto;
    painel.style.transform = `translateX(${pos * alvo.lado}px)`;
    painel.style.setProperty('--mm-arrasto', String(Math.min(1, Math.max(0, pos / largura))));
    amostras.push({ t: event.timeStamp, p: pos });
    while (amostras.length > 2 && event.timeStamp - amostras[0].t > 100) amostras.shift();
  },
  { passive: false }
);

function terminarArrasto(event) {
  const a = arrasto;
  arrasto = null;
  if (!a || !a.decidido) return;
  const { painel, alvo, largura, amostras, pos } = a;
  const primeira = amostras[0];
  const ultima = amostras[amostras.length - 1];
  const dt = Math.max(1, ultima.t - primeira.t);
  // Dedo parado antes de soltar (mais de 100 ms sem mexer): a velocidade é zero, não a do último movimento
  const parado = event && event.timeStamp - ultima.t > 100;
  const velocidade = parado ? 0 : ((ultima.p - primeira.p) / dt) * 1000;
  // A decisão sai de onde o gesto iria parar, não de onde o dedo soltou
  if (pos > 0 && pos + projetar(velocidade) > largura / 2) {
    painel.classList.add('mm-solto');
    alvo.fechar(painel);
    return;
  }
  // Volta para o lugar, a partir de onde está
  if (menosMovimento() || pos === 0) {
    soltarPainel(painel);
    return;
  }
  const volta = painel.animate(
    [{ transform: `translateX(${pos * alvo.lado}px)` }, { transform: 'translateX(0px)' }],
    { duration: 300, easing: MOLA_SEM_QUIQUE }
  );
  const acompanhar = () => {
    if (volta.playState !== 'running') return;
    const agora = Math.abs(deslocamentoAtual(painel));
    painel.style.setProperty('--mm-arrasto', String(Math.min(1, agora / largura)));
    requestAnimationFrame(acompanhar);
  };
  painel.style.removeProperty('transform');
  requestAnimationFrame(acompanhar);
  volta.finished.then(() => soltarPainel(painel)).catch(() => {});
}

document.addEventListener('touchend', terminarArrasto, { passive: true });
document.addEventListener('touchcancel', terminarArrasto, { passive: true });

/* ------------------------------------------------------------------ */
/* Janela de compra rápida nasce de onde a cliente tocou              */
/* (apple-design, seção 7). Guarda o ponto do último toque e, quando  */
/* a janela abre, põe a origem do crescimento nele (--mm-origem). O   */
/* CSS está em snippets/quick-add-modal-styles.liquid.                */
/* ------------------------------------------------------------------ */

let ultimoToque = null;
document.addEventListener(
  'pointerdown',
  (event) => {
    ultimoToque = { x: event.clientX, y: event.clientY, t: Date.now() };
  },
  { capture: true, passive: true }
);

new MutationObserver((mudancas) => {
  for (const m of mudancas) {
    const janela = m.target;
    if (!(janela instanceof HTMLDialogElement) || !janela.open || !janela.classList.contains('quick-add-modal')) continue;
    if (!ultimoToque || Date.now() - ultimoToque.t > 3000) {
      janela.style.removeProperty('--mm-origem');
      continue;
    }
    const r = janela.getBoundingClientRect();
    janela.style.setProperty('--mm-origem', `${Math.round(ultimoToque.x - r.left)}px ${Math.round(ultimoToque.y - r.top)}px`);
  }
}).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['open'] });
